/**
 * Mermaid 代码预处理器 - 修复 LLM 常见的语法错误
 * 用于 mermaid.parse() 错误检测和 mermaid.render() 预览渲染
 */

/** 转义正则表达式特殊字符 */
export const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Gantt after 引用不匹配信息 */
export interface GanttAfterMismatch {
  /** 原始 after 引用的名称（如 "需求对接"） */
  originalRef: string;
  /** 修正后的任务名（如 "功能详细设计"），无法修正时为 null */
  resolvedRef: string | null;
}

/**
 * 检查 Gantt 图中 after 引用的有效性
 * LLM 常将 `after section名` 作为任务依赖，但 Mermaid Gantt 的 after 只能引用任务名
 * 此函数找出所有引用了不存在的任务名的 after 语句
 */
export const getGanttAfterMismatches = (code: string): GanttAfterMismatch[] => {
  if (!/^gantt/i.test(code)) return [];

  const lines = code.split('\n');
  const sectionTasks: { section: string; tasks: string[] }[] = [];
  let currentSection: string | null = null;
  const taskDefRegex = /^\s*([^:]+?)\s*:\s*(.+)$/;
  const sectionRegex = /^\s*section\s+(.+?)\s*$/i;

  for (const line of lines) {
    const sectionMatch = line.match(sectionRegex);
    if (sectionMatch) {
      currentSection = sectionMatch[1].trim();
      sectionTasks.push({ section: currentSection, tasks: [] });
      continue;
    }
    if (currentSection) {
      const taskMatch = line.match(taskDefRegex);
      if (taskMatch) {
        const taskName = taskMatch[1].trim();
        sectionTasks[sectionTasks.length - 1].tasks.push(taskName);
        // 同时提取任务 ID 别名（如 :a1, 2025-06-01, 7d 中的 a1）
        // 右侧内容按逗号分割，第一段非日期、非 after/before 的即为任务 ID
        const rightSide = taskMatch[2].trim();
        const firstPart = rightSide.split(',')[0].trim();
        // 不是日期格式（YYYY-MM-DD），不是 after/before 关键字，即为任务 ID
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (firstPart && !dateRegex.test(firstPart) && !/^(after|before)\b/i.test(firstPart)) {
          sectionTasks[sectionTasks.length - 1].tasks.push(firstPart);
        }
      }
    }
  }

  const allTaskNames = new Set<string>();
  for (const st of sectionTasks) {
    for (const t of st.tasks) allTaskNames.add(t);
  }

  const sectionFirstTask = new Map<string, string>();
  for (const st of sectionTasks) {
    if (st.tasks.length > 0) sectionFirstTask.set(st.section, st.tasks[0]);
  }

  const mismatches: GanttAfterMismatch[] = [];
  const afterRefRegex = /\bafter\s+([^\s,]+)/gi;

  for (const line of lines) {
    let match: RegExpExecArray | null;
    // 重置 regex lastIndex（全局匹配需要）
    afterRefRegex.lastIndex = 0;
    while ((match = afterRefRegex.exec(line)) !== null) {
      const ref = match[1].trim();
      if (!allTaskNames.has(ref)) {
        let resolved: string | null = null;
        // 精确匹配 section 名
        const taskFromSection = sectionFirstTask.get(ref);
        if (taskFromSection) {
          resolved = taskFromSection;
        } else {
          // 模糊匹配 section 名
          for (const st of sectionTasks) {
            if (st.section.includes(ref) && st.tasks.length > 0) {
              resolved = st.tasks[0];
              break;
            }
          }
        }
        if (!resolved) {
          // 模糊匹配任务名
          for (const t of Array.from(allTaskNames)) {
            if (t === ref || t.includes(ref) || ref.includes(t)) {
              resolved = t;
              break;
            }
          }
        }
        mismatches.push({ originalRef: ref, resolvedRef: resolved });
      }
    }
  }

  return mismatches;
};

/** 预处理 mermaid 代码，修复 LLM 常见的语法错误 */
export const preprocessMermaidCode = (code: string): string => {
  let fixed = code.trim();

  // 0. 修复中文标点符号问题：中文句号、中文破折号等替换为英文符号
  // 常见问题：LLM 可能输出 `A -。-> C`（中文句号）或 `A ——> C`（中文破折号）
  // 注意：不转换全角括号（），因为 mermaid 将 ( ) 视为形状标记，节点文本中的 () 会导致解析失败
  // 全角（）作为普通文本字符可以安全存在于节点文本中
  fixed = fixed
    .replace(/。/g, '.')       // 中文句号 → 英文句号
    .replace(/——/g, '--')     // 中文破折号 → 英文双横线
    .replace(/；/g, ';')      // 中文分号 → 英文分号
    .replace(/：/g, ':');     // 中文冒号 → 英文冒号

  // 1. 修复中文节点 ID 问题：将 `中文描述 --> 中文描述` 自动转为 `A[中文描述] --> B[中文描述]`
  // 匹配 flowchart/graph 中未被方括号包裹的中文节点
  if (/^(graph|flowchart)\s/i.test(fixed)) {
    const lines = fixed.split('\n');
    let idCounter = 0;
    const idMap = new Map<string, string>();
    const getNodeId = (text: string): string => {
      if (!idMap.has(text)) {
        idMap.set(text, `N${idCounter++}`);
      }
      return idMap.get(text)!;
    };

    // 只处理明显的纯中文节点（没有方括号包裹的）
    // 对每一行，如果出现中文文本后紧跟箭头或行尾，且前面不是 [ 或 ( 则添加方括号
    let processed = '';
    for (const line of lines) {
      let newLine = line;
      // 跳过 subgraph / end 行，不处理其中的中文文本
      if (/^\s*(subgraph|end)\b/i.test(newLine)) {
        processed += newLine + '\n';
        continue;
      }
      // 替换模式：中文节点名后面跟箭头，且节点名未被 [ 或 ( 包裹
      // 例如：需求分析 --> 方案设计  →  N0[需求分析] --> N1[方案设计]
      newLine = newLine.replace(
        /(^|\s|-->|---|\.\.->|==>|-.->|\)|\])\s*([\u4e00-\u9fa5][\u4e00-\u9fa5\w\u3000-\u303f\uff00-\uffef()（）]*?)\s*(-->|---|\.\.->|==>|-.->|$)/g,
        (match, before, text, after) => {
          // 确保 text 不是已经在方括号内
          if (text.match(/^[\u4e00-\u9fa5]/) && !text.includes('[')) {
            const id = getNodeId(text.trim());
            return `${before}${id}[${text.trim()}]${after}`;
          }
          return match;
        }
      );
      processed += newLine + '\n';
    }
    fixed = processed.trim();
  }

  // 2. 修复方括号节点文本中包含 {} 导致 mermaid 误解析为菱形节点的问题
  // 例如：C4[合并得 δS_g ∝ (R_{μν} - ½g_{μν}R)δg^{μν}]
  // →     C4["合并得 δS_g ∝ (R_{μν} - ½g_{μν}R)δg^{μν}"]
  // 规则：如果方括号 [...] 内包含 { 或 }，将 [...] 替换为 ["..."]
  fixed = fixed.replace(
    /(\b\w+)\[([^\]]*\{[^\]]*\}[^\]]*)\]/g,
    (_match: string, id: string, text: string) => {
      return `${id}["${text}"]`;
    }
  );

  // 3. 修复圆括号节点文本中包含 {} 的问题（类似处理）
  // 例如：A(δg^{μν}) → A("δg^{μν}")
  fixed = fixed.replace(
    /(\b\w+)\(([^)]*\{[^)]*\}[^)]*)\)/g,
    (_match: string, id: string, text: string) => {
      return `${id}("${text}")`;
    }
  );

  // 4. 修复 erDiagram 属性描述中的特殊字符问题
  // erDiagram 属性格式：属性名 类型 [描述]
  // 描述中包含特殊字符（.、%、<、>、(、) 等）时需用双引号包裹
  // 例如：CORR float 大于0.7 → CORR float "大于0.7"
  //       连续性评分 float 大于85% → 连续性评分 float "大于85%"
  if (/^erDiagram/i.test(fixed)) {
    fixed = fixed.replace(
      /^(\s+)([^\s{]+)\s+(string|float|int|boolean|date|datetime|varchar|number)\s+([^"\s].+?)$/gm,
      (_match: string, indent: string, attrName: string, attrType: string, desc: string) => {
        // 如果描述中包含特殊字符，需要加双引号
        if (/[.%<>()[\]{}]/.test(desc)) {
          return `${indent}${attrName} ${attrType} "${desc.trim()}"`;
        }
        return _match;
      }
    );
  }

  // 5. 修复 Gantt 图中 after 引用错误：
  //    LLM 常将 `after section名` 作为任务依赖，但 Mermaid Gantt 的 after 只能引用任务名
  //    例如：after 需求对接 → 需要找到 "需求对接" section 下的第一个任务来引用
  if (/^gantt/i.test(fixed)) {
    const mismatches = getGanttAfterMismatches(fixed);
    for (const m of mismatches) {
      if (m.resolvedRef) {
        // 将 after 原引用名 → after 修正后的任务名
        fixed = fixed.replace(
          new RegExp(`\\bafter\\s+${escapeRegex(m.originalRef)}\\b`, 'g'),
          `after ${m.resolvedRef}`
        );
      }
    }
  }

  return fixed;
};
